import * as Sentry from "@sentry/react";
import { esNativo, plataformaNativa } from "../lib/nativo";

/**
 * Monitoreo de errores del panel web (Sentry).
 *
 * Por qué existe: el backend ya reportaba sus 500 desde julio, pero lo que se
 * rompía EN EL NAVEGADOR no dejaba rastro en ningún lado. Si al organizador se
 * le queda una pantalla en blanco cargando un resultado en la cancha, la única
 * forma de enterarse era que avisara. Ahora llega solo, con la pantalla, el
 * usuario y la versión.
 *
 * El DSN va HARDCODEADO como default a propósito, igual que el Client ID de
 * Google en render.yaml y la URL productiva en build.gradle.kts: **no es un
 * secreto** (viaja dentro del bundle, cualquiera lo ve en el navegador) y sólo
 * sirve para MANDAR errores, no para leerlos. Dejarlo sólo como variable de
 * entorno significaba que si alguien olvidaba cargarla en Render, el monitoreo
 * quedaba apagado EN SILENCIO — que es exactamente el problema que vinimos a
 * resolver. Se puede overridear con VITE_SENTRY_DSN.
 */
const DEFAULT_DSN =
  "https://4f1e10f7bc85666c8b081d8438cc27a2@o4511776034193408.ingest.us.sentry.io/4511904203603968";

const DSN = import.meta.env.VITE_SENTRY_DSN ?? DEFAULT_DSN;

export function initSentry(): void {
  // Sólo en el build de producción: si reportara desde `npm run dev`, cada
  // experimento roto del desarrollo ensuciaría el panel y el día que pase algo
  // de verdad no se distinguiría del ruido.
  if (!import.meta.env.PROD || !DSN) return;
  // Un build de producción servido en la máquina de uno (vite preview, para
  // revisar antes de subir) NO es producción: el 17-09-2026 una prueba local
  // mandó al panel un "Failed to register a ServiceWorker" con URL
  // localhost:4173, marcado como production. Eso confunde justo cuando hay que
  // mirar rápido si se rompió algo de verdad.
  //
  // PERO LA APP DE iPHONE TAMBIÉN SE SIRVE DESDE "localhost": el contenedor
  // carga el bundle en `capacitor://localhost`, así que este filtro la dejaba
  // afuera y NINGÚN error de la app llegó nunca —ni los que se mandaban a
  // propósito, como la sonda del ingreso nativo. Se notó el 3-11-2026: una
  // testera con el ingreso con Apple colgado, y en el panel nada. Adentro de
  // la app no existe el "vite preview" que este filtro quería evitar.
  const host = window.location.hostname;
  const enApp = esNativo();
  if (!enApp && (host === "localhost" || host === "127.0.0.1" || host === "::1" || host.endsWith(".local"))) return;

  Sentry.init({
    dsn: DSN,
    // La app y la web comparten proyecto; el entorno los separa en el panel
    // sin tener que mirar el user agent de cada evento.
    environment: enApp ? "app-" + plataformaNativa() : "production",
    // Sólo errores. El tracing de performance multiplica el volumen de eventos
    // y come la cuota gratis (5.000/mes) sin darnos nada que hoy necesitemos.
    tracesSampleRate: 0,
    // Ruido conocido que NO es un problema de FORJA:
    ignoreErrors: [
      // El usuario perdió señal en la cancha a mitad de un fetch. Es esperable
      // en un teléfono al costado de un campo de juego, no un bug.
      "Failed to fetch",
      "NetworkError when attempting to fetch resource",
      "Load failed",
      // Safari/iOS al navegar mientras hay una request en vuelo.
      "cancelled",
      "AbortError",
      // Extensiones del navegador inyectando código en la página.
      "ResizeObserver loop completed with undelivered notifications",
      // Una extensión hablándole a una pestaña que ya no existe (01-10-2026,
      // en /login sin usuario). FORJA no usa la API de extensiones.
      "runtime.sendMessage",
    ],
    // Cualquier error cuyo stack nace dentro de una extensión no es nuestro.
    denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i, /^safari(-web)?-extension:\/\//i],
    beforeSend(event) {
      // La sesión de FORJA vive en localStorage; que un stack trace o una URL
      // se lleve un token al panel sería filtrar credenciales.
      if (event.request?.url) {
        event.request.url = event.request.url.replace(/([?&](token|access_token)=)[^&]+/gi, "$1[filtrado]");
      }
      return event;
    },
  });
}

/**
 * Reporta un error a mano (lo usa el ErrorBoundary). No-op si Sentry no está
 * inicializado, así que se puede llamar sin condicionales.
 */
export function captureException(error: unknown, context?: Record<string, unknown>): void {
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/**
 * Etiqueta los errores con QUIÉN los sufrió. Sin esto, un incidente dice "algo
 * falló" pero no a quién llamar para preguntarle qué estaba haciendo.
 */
export function setSentryUser(user: { id: string; email?: string | null } | null): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({ id: user.id, email: user.email ?? undefined });
}
